import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@jsverse/transloco';
import assertNever from 'assert-never';
import { Subject, takeUntil, tap, throttleTime } from 'rxjs';

import { DeviceClaimingApiService } from '@shure/cloud/device-management/devices/data-access';
import {
	AddDeviceMethod,
	DeviceManagementAnalyticsEvents,
	DeviceManagementAnalyticsService
} from '@shure/cloud/device-management/shared/analytics';
import { DeviceClaimProgress, DeviceDetection } from '@shure/cloud/shared/models/devices';
import { PipesModule } from '@shure/cloud/shared/ui/pipes';
import { PrismProgressIndicatorComponent } from '@shure/prism-angular-components';
import { DeviceModel, DeviceStatus } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';

import {
	AddDevicesDialogService,
	DeviceClaimMethods,
	MacSerialNumberDevice
} from '../../../../services/add-devices-dialog.service';

import { LocalNetworkConfirmationTableComponent } from './local-network-confirmation-table/local-network-confirmation-table.component';
import { MacSerialConfirmationTableComponent } from './mac-serial-confirmation-table/mac-serial-confirmation-table.component';

@Component({
	selector: 'sh-step3-confirmation',
	imports: [
		CommonModule,
		TranslocoModule,
		PipesModule,
		LocalNetworkConfirmationTableComponent,
		MacSerialConfirmationTableComponent,
		PrismProgressIndicatorComponent
	],
	templateUrl: './step3-confirmation.component.html',
	styleUrls: ['./step3-confirmation.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class Step3ConfirmationComponent implements OnDestroy, AfterViewInit {
	public destroy$: Subject<void> = new Subject<void>();

	public deviceClaimMethod: DeviceClaimMethods | undefined;
	public localNetworkDevicesToClaim: DeviceClaimProgress<DeviceDetection>['devices'][0][] = [];
	public macSerialDevicesToClaim: DeviceClaimProgress<MacSerialNumberDevice>['devices'][0][] = [];
	public claimOpInProgress$ = this.deviceClaimingService.claimOpInProgress$
		.asObservable()
		.pipe(
			tap((result) =>
				this.logger.information(`Claim In Progress (${this.deviceClaimMethod})`, `value: ${result}`)
			)
		);
	public currentClaimProgress: DeviceClaimProgress = {
		done: false,
		stats: {
			totalToDo: 0,
			success: 0,
			inProgress: 0,
			failed: 0
		},
		devices: []
	};
	private readonly claimMethod = toSignal(this.addDevicesDialogService.deviceDiscoveryMethod$);
	private readonly devicesClaimedLocalNetworkScanning = toSignal(
		this.addDevicesDialogService.detectedClaimableDevices$
	);
	private readonly devicesClaimedMacSerial = toSignal(this.addDevicesDialogService.macSerialNumbersToClaim$);
	private logger: ILogger;

	constructor(
		private readonly addDevicesDialogService: AddDevicesDialogService,
		private readonly analyticsService: DeviceManagementAnalyticsService,
		protected readonly deviceClaimingService: DeviceClaimingApiService,
		private changeDetectorRef: ChangeDetectorRef,
		private _logger: ILogger
	) {
		this.logger = this._logger.createScopedLogger('AddDevicesDialogStep3');
	}

	public ngAfterViewInit(): void {
		setTimeout(() => {
			this.addDevicesDialogService.deviceDiscoveryMethod$
				.pipe(takeUntil(this.destroy$))
				.subscribe((claimMethod) => {
					if (claimMethod !== undefined) {
						this.deviceClaimMethod = claimMethod;
						this.startClaim(claimMethod);
					} else {
						throw Error('Unexpected undefind claimmethod');
					}
				});
		});

		this.sendDevicesAddedEvent();
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.addDevicesDialogService.setDetectedClaimableDevices([]);
	}

	public doDeviceDetectionClaim(devices: DeviceDetection[]): void {
		this.deviceClaimingService
			.claimDevicesThrottled$(devices)
			.pipe(takeUntil(this.destroy$))
			.subscribe((progress) => {
				this.localNetworkDevicesToClaim = [...progress.devices];
				this.currentClaimProgress = progress;
				this.changeDetectorRef.detectChanges();
			});
	}

	public doMacSerialClaiming(devices: MacSerialNumberDevice[]): void {
		this.deviceClaimingService
			.claimDevicesByMacSerialThrottled$(devices)
			.pipe(takeUntil(this.destroy$))
			.subscribe((progress) => {
				this.macSerialDevicesToClaim = [...progress.devices];
				this.currentClaimProgress = { ...progress };
				this.changeDetectorRef.detectChanges();
			});
	}

	private startClaim(claimMethod: DeviceClaimMethods): void {
		if (claimMethod === 'ClaimByTokenOnLocalNetwork') {
			this.addDevicesDialogService.detectedClaimableDevices$
				// because there can be a flurry of "progress" reports emitted,
				// and it's not possible to report them all, apply a
				// throttle so the UI looks better.
				.pipe(throttleTime(1000), takeUntil(this.destroy$))
				.subscribe((devicesToClaim) => {
					this.doDeviceDetectionClaim(devicesToClaim);
				});
		} else {
			this.addDevicesDialogService.macSerialNumbersToClaim$
				// because there can be a flurry of "progress" reports emitted,
				// and it's not possible to report them all, apply a
				// throttle so the UI looks better.
				.pipe(throttleTime(1000), takeUntil(this.destroy$))
				.subscribe((macSerialNumbers) => {
					this.doMacSerialClaiming(macSerialNumbers);
				});
		}
	}

	private sendDevicesAddedEvent(): void {
		const claimMethod = this.claimMethod();

		if (!claimMethod) {
			return;
		}

		switch (claimMethod) {
			case 'ClaimByTokenOnLocalNetwork':
				this.sendLocalNetworkScanningAddEvent();
				break;
			case 'ClaimByMacSerialSingle':
				this.sendMacSerialAddEvent(AddDeviceMethod.MacSerialIndividual);
				break;
			case 'ClaimByMacSerialCsv':
				this.sendMacSerialAddEvent(AddDeviceMethod.MacSerialCsvFile);
				break;
			case 'ClaimByQrCode':
				this.sendMacSerialAddEvent(AddDeviceMethod.QrCode);
				break;
			default:
				assertNever(claimMethod);
		}
	}

	private sendLocalNetworkScanningAddEvent(): void {
		const analyticsDevices = (this.devicesClaimedLocalNetworkScanning() ?? []).map((device) => ({
			model: Object.values(DeviceModel).includes(<DeviceModel>device.device.model)
				? <DeviceModel>device.device.model
				: DeviceModel.UNKNOWN,
			mac: device.device.macAddress,
			serial: device.device.serialNumber,
			// eslint-disable-next-line @typescript-eslint/naming-convention
			hardware_id: device.device.hardwareId,
			// eslint-disable-next-line @typescript-eslint/naming-convention
			network_status: DeviceStatus.Online // Assumed online if detected on local network
		}));

		this.analyticsService.sendEvent(DeviceManagementAnalyticsEvents.AddDevicesDialogDevicesAdded, {
			method: AddDeviceMethod.LocalNetworkScanning,
			devices: analyticsDevices
		});
	}

	private sendMacSerialAddEvent(
		claimMethod: AddDeviceMethod.MacSerialIndividual | AddDeviceMethod.MacSerialCsvFile | AddDeviceMethod.QrCode
	): void {
		const analyticsDevices = (this.devicesClaimedMacSerial() ?? []).map((device) => ({
			mac: device.device.macAddress,
			serial: device.device.serialNumber
		}));

		this.analyticsService.sendEvent(DeviceManagementAnalyticsEvents.AddDevicesDialogDevicesAdded, {
			method: claimMethod,
			devices: analyticsDevices
		});
	}
}
